import React, { Component } from 'react';
import { DateCalendar } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Box, Button } from '@mui/material';
import moment from 'moment';
import i18n from '../../i18n';
import { Day } from './Day';

const initialState = {
    startDate: null,
    endDate: null
};

export default class CustomCalendar extends Component {
    onChange = () => {};
    limit = 0;
    constructor(props) {
        super(props);
        this.state = {
            firstCalendar: initialState,
            secondCalendar: initialState,
            shortcuts: props.shortcuts || []
            // shortcuts={[{ label: 'today', days: 1 }, { label: 'twoDays', days: 2 }]}
        };
        this.onChange = props.onChange;
        this.limit = props.limit;
    }

    componentDidMount() {
        const { startDate, endDate } = this.props.value;

        this.convertDateToCalendarRange(startDate, endDate);
    }
    componentDidUpdate(prevProps, prevState) {
        const { startDate, endDate } = this.props.value;

        if (
            startDate !== prevProps.value.startDate ||
            endDate !== prevProps.value.endDate
        ) {
            this.convertDateToCalendarRange(startDate, endDate);
        }
    }

    convertDateToCalendarRange = (startDate, endDate) => {
        const currentMonth = moment().month();
        const startMonth = startDate ? startDate.month() : null;
        const endMonth = endDate ? endDate.month() : null;

        if (!startDate && !endDate) {
            return;
        }

        if (startMonth === endMonth) {
            // If the start month is the current month or a past month
            if (startMonth === currentMonth || startMonth < currentMonth) {
                this.setState({
                    firstCalendar: { startDate, endDate },
                    secondCalendar: { ...initialState }
                });
            } else {
                // If the start month is a future month
                this.setState({
                    firstCalendar: { ...initialState },
                    secondCalendar: { startDate, endDate }
                });
            }
        }
        // If the start month is different from the end month
        else if (!endMonth && startMonth > currentMonth) {
            this.setState({
                firstCalendar: { ...initialState },
                secondCalendar: { startDate, endDate }
            });
        } else {
            // General case for different start and end months
            this.setState({
                firstCalendar: { startDate, endDate: null },
                secondCalendar: { startDate: null, endDate }
            });
        }
    };

    handleDateChange = (newValue, calendarId) => {
        if (!newValue) {
            return;
        }

        const normalizedValue = moment(newValue).startOf('day');
        const { firstCalendar, secondCalendar } = this.state;

        const selectedCalendar =
            calendarId === 'firstCalendar' ? firstCalendar : secondCalendar;
        const otherCalendar =
            calendarId === 'firstCalendar' ? secondCalendar : firstCalendar;

        const hasLimit = this.limit > 0;

        if (hasLimit) {
            const selectedStart = (selectedCalendar.startDate || otherCalendar.startDate)?.clone().startOf('day');

            if (selectedStart) {
                const monthDays = selectedStart.daysInMonth();
                const effectiveLimit = Math.min(this.limit, monthDays);

                const diff = normalizedValue.diff(selectedStart, 'days') + 1;
                if (diff > effectiveLimit) {
                    return;
                }
            }
        }

        // if chosen date is before startDate in any calendar
        if (
            (selectedCalendar.startDate &&
                normalizedValue.isBefore(selectedCalendar.startDate)) ||
            (otherCalendar.startDate &&
                normalizedValue.isBefore(otherCalendar.startDate))
        ) {
            this.setState(
                {
                    [calendarId]: {
                        startDate: normalizedValue,
                        endDate: null
                    },
                    [calendarId === 'firstCalendar'
                        ? 'secondCalendar'
                        : 'firstCalendar']: {
                        startDate: null,
                        endDate: null
                    }
                },
                this.handleUpdateDate
            );
            return;
        }

        if (
            otherCalendar.startDate &&
            otherCalendar.endDate &&
            normalizedValue.isAfter(otherCalendar.endDate)
        ) {
            this.setState(
                {
                    [calendarId]: {
                        startDate: null,
                        endDate: normalizedValue
                    },
                    [calendarId === 'firstCalendar'
                        ? 'secondCalendar'
                        : 'firstCalendar']: {
                        startDate: otherCalendar.startDate,
                        endDate: null
                    }
                },
                this.handleUpdateDate
            );
            return;
        }

        const shouldSetEndDate =
            (!otherCalendar.endDate && otherCalendar.startDate) ||
            selectedCalendar.startDate ||
            (otherCalendar.startDate && otherCalendar.endDate < normalizedValue);

        const shouldSetStartDate =
            !otherCalendar.startDate && !selectedCalendar.startDate;

        const newCalendarState = {
            ...selectedCalendar,
            startDate: shouldSetStartDate
                ? normalizedValue
                : selectedCalendar.startDate,
            endDate: shouldSetEndDate
                ? normalizedValue
                : selectedCalendar.endDate
        };

        this.setState(
            {
                [calendarId]: newCalendarState
            },
            this.handleUpdateDate
        );
    };


    handleUpdateDate = () => {
        const { firstCalendar, secondCalendar } = this.state;
        let startDate = null;
        let endDate = null;

        startDate = firstCalendar.startDate || secondCalendar.startDate;
        endDate = firstCalendar.endDate || secondCalendar.endDate;

        this.onChange({ startDate, endDate });
    };

    handleShortcutClick = (daysOrLabel) => {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        let startDate;
        let endDate;

        if (typeof daysOrLabel === 'string') {
            switch (daysOrLabel) {
            case 'today':
                startDate = endDate = moment.tz(tz).startOf('day');
                break;
            case 'thisWeek':
                startDate = moment.tz(tz).startOf('week'); // Monday
                endDate = moment.tz(tz).endOf('week'); // Sunday
                break;
            case 'thisMonth':
                startDate = moment.tz(tz).startOf('month');
                endDate = moment.tz(tz).endOf('month');
                break;
            default:
                return; // ignore unknown labels
            }
        } else if (typeof daysOrLabel === 'number') {
            endDate = moment.tz(tz).startOf('day'); // today
            startDate = moment(endDate).subtract(daysOrLabel - 1, 'days');
        } else {
            return;
        }

        let firstCalendar = { startDate, endDate };
        let secondCalendar = { ...initialState };

        if (startDate.month() !== endDate.month()) {
            firstCalendar = {
                startDate,
                endDate: null
            };
            secondCalendar = {
                startDate: null,
                endDate: endDate
            };
        }

        this.setState({
            firstCalendar,
            secondCalendar
        });

        this.onChange({ startDate, endDate });
    };


    handleClear = () => {
        this.setState({
            firstCalendar: initialState,
            secondCalendar: initialState
        });
        this.onChange({ startDate: null, endDate: null });
    };

    render() {
        const { firstCalendar, secondCalendar } = this.state;

        const locale = i18n.language === 'en' ? 'en-gb' : i18n.language;
        const nextMonth = moment().add(1, 'months').startOf('month');

        // Props for the Day component
        const firstDayProps = {
            startDate: firstCalendar.startDate,
            endDate:
                secondCalendar.endDate && !secondCalendar.startDate
                    ? secondCalendar.endDate
                    : firstCalendar.endDate
        };
        const secondDayProps = {
            startDate:
                firstCalendar.startDate && !firstCalendar.endDate
                    ? firstCalendar.startDate
                    : secondCalendar.startDate,
            endDate: secondCalendar.endDate,
            limit: this.limit
        };

        return (
            <Box display="flex" flexDirection="column" alignItems="center">
                <Box display="flex" gap={2} flexWrap="wrap">
                    <LocalizationProvider
                        dateAdapter={AdapterMoment}
                        adapterLocale={locale}
                    >
                        <DateCalendar
                            id="firstCalendar"
                            value={firstCalendar.startDate}
                            onChange={(nv) =>
                                this.handleDateChange(nv, 'firstCalendar')
                            }
                            slots={{ day: Day }}
                            slotProps={{
                                day: () => firstDayProps
                            }}
                        />
                        <DateCalendar
                            id="secondCalendar"
                            value={secondCalendar.startDate}
                            referenceDate={nextMonth}
                            // defaultCalendarMonth={nextMonth}
                            onChange={(nv) =>
                                this.handleDateChange(nv, 'secondCalendar')
                            }
                            slots={{ day: Day }}
                            slotProps={{
                                day: () => secondDayProps
                            }}
                        />
                    </LocalizationProvider>
                </Box>
                {this.state.shortcuts && this.state.shortcuts.length > 0 && (
                    <Box
                        display="flex"
                        gap={2}
                        mt={2}
                        flexWrap="wrap"
                        alignItems="stretch"
                    >
                        {this.state.shortcuts.map((shortcut) => (
                            <Button
                                key={shortcut.days}
                                sx={{ flexGrow: 1 }}
                                variant="outlined"
                                onClick={() =>
                                    this.handleShortcutClick(shortcut.days)
                                }
                            >
                                {i18n.t(`shortcuts.${shortcut.label}`)}
                            </Button>
                        ))}
                        <Button variant="outlined" onClick={this.handleClear}>
                            {i18n.t('shortcuts.clear')}
                        </Button>
                    </Box>
                )}
            </Box>
        );
    }
}
